// STRATUL 2D AL INSTALAȚIILOR, pe planul de nivel: traseele nivelurilor
// desenate, în culoarea stratului lor (a sistemului; la cabluri, a tipului de
// circuit) și la lățimea lor reală (în metri, ca restul planului), punctat ce
// trece prin șapă; coloanele ca cercuri, echipamentele ca dreptunghiuri.
// Cablurile paralele pe același perete se desenează unul lângă altul
// (`decalaje`), ca tuburile lor pe planșă — nu unul peste altul; cablurile
// apartamentelor de pe coloană au în model fiecare locul lui pe pat, iar patul
// (gri, sub ele) se desenează cât lățimea lui. Stă în același `<g>` cu căile
// de evacuare (`plan/EvacuareTrasee`), deci în cadrul planului, fără nicio
// transformare.

import { useMemo, type MouseEvent } from "react";
import { useInstalatii } from "./context";
import {
  CATEGORII, CULOARE, CULOARE_LIPSA, TOATE_STRATURILE, culoareSegment, decalaje, pieseFaraRacord, stratul,
  type Segment, type Strat,
} from "./model";

/** Lățimea minimă desenată: o țeavă DN 16 ar fi o linie de 2 cm. */
const LATIME_MIN_M = 0.035;
/** Cablurile se desenează cât tubul lor, dar nu sub 2 cm, ca să se vadă. */
const LATIME_MIN_CABLU_M = 0.02;
const CULOARE_ALES = "#ffc107";
/** Conturul apartamentelor: punctat, în culoarea lor; al celui ales, plin. */
const CULOARE_APARTAMENT = "#5c6bc0";
/** Cât de stins se desenează ce nu e al apartamentului ales. */
const ESTOMPAT = 0.12;
/** Patul de cabluri: suportul, nu un traseu — gri, sub cabluri. */
const CULOARE_PAT = "#9a948c";

const vertical = (s: Segment) => s.puncte.length === 2
  && Math.hypot(s.puncte[0][0] - s.puncte[1][0], s.puncte[0][1] - s.puncte[1][1]) < 1e-6;

export default function StratPlanInstalatii({ niveluri, corpuri, cotaJos, cotaSus }: {
  /** `StoreyPlan.id`-urile desenate acum. */
  niveluri: ReadonlySet<string>;
  /** Corpurile (`StoreyPlan.owner`) desenate acum: coloanele n-au nivelul
   *  curent, deci numai corpul le spune dacă sunt ale planului de pe ecran. */
  corpuri?: ReadonlySet<string>;
  /** Fereastra de cote a nivelului, ca să se vadă doar coloanele care trec prin el. */
  cotaJos: number;
  cotaSus: number;
}) {
  const inst = useInstalatii();
  const m = inst?.model;
  const dec = useMemo(() => (m ? decalaje(m.segmente) : new Map<string, [number, number][]>()), [m]);
  if (!inst || !m) return null;
  const alege = (id: string) => (e: MouseEvent) => { e.stopPropagation(); inst.alege(id); };
  const ordine: Strat[] = TOATE_STRATURILE.filter((s) => inst.vizibile.has(s));
  const alCorpului = (s: Segment) => !corpuri || (!!s.building && corpuri.has(s.building));
  const fara = pieseFaraRacord(m);
  // «doar apartamentul ales»: ce nu e al lui se estompează, nu dispare — ca
  // drumul până la coloana clădirii să rămână de citit
  const flatAles = inst.doarApartamentul ? inst.apartamentAles : null;
  const estompat = (flat: string | null | undefined) => !!flatAles && flat !== flatAles && !!flat;
  // un echipament e al sistemului lui: se vede cât timp e aprins vreun strat al categoriei lui
  const sistemAprins = (sistem: string) =>
    CATEGORII.some((c) => c.straturi.some((x) => inst.vizibile.has(x) && (x === sistem || x.startsWith(`${sistem}.`))));
  return (
    <g className="inst-plan">
      {/* piesele fără scurgere sau fără apă: rama roșie peste piesa din plan —
          lipsa se arată pe piesă, nu printr-o țeavă inventată */}
      {m.piese.filter((p) => fara.has(p.id) && p.storey && niveluri.has(p.storey)).map((p) => (
        <rect key={p.id} x={-p.dim[0] / 2} y={-p.dim[1] / 2} width={p.dim[0]} height={p.dim[1]}
              transform={`translate(${p.centru[0]},${p.centru[1]}) rotate(${p.deg})`}
              fill={CULOARE_LIPSA} fillOpacity={0.25} stroke={CULOARE_LIPSA} strokeWidth={0.04} />
      ))}
      {/* contururile apartamentelor: fiecare se citește separat, de la racordul
          lui la ultima piesă; cel ales, cu contur plin */}
      {inst.apartamente && (m.apartamente ?? []).filter((a) => a.storey && niveluri.has(a.storey)).map((a) => (
        <polygon key={a.id} points={a.poly.map((p) => `${p[0]},${p[1]}`).join(" ")} fill="none"
                 stroke={CULOARE_APARTAMENT} strokeWidth={a.id === inst.apartamentAles ? 0.08 : 0.04}
                 strokeDasharray={a.id === inst.apartamentAles ? undefined : "0.3 0.15"} opacity={0.8}
                 pointerEvents="none" />
      ))}
      {ordine.map((strat) => (
        <g key={strat}>
          {m.segmente.filter((s) => stratul(s) === strat && s.rol !== "lipsa" && !vertical(s) && s.storey
            && niveluri.has(s.storey))
            // patul se desenează înaintea cablurilor de pe el: e sub ele
            .sort((a, b) => Number(b.rol === "pat") - Number(a.rol === "pat"))
            .map((s) => {
              const ales = inst.ales === s.id || inst.evidentiate.has(s.id);
              const pat = s.rol === "pat";
              const cablu = s.sistem === "electrice" && !pat;
              const lat = cablu ? Math.max(s.d_m, LATIME_MIN_CABLU_M) : Math.max(s.sectiune?.l ?? s.d_m, LATIME_MIN_M);
              const culoare = ales ? CULOARE_ALES : pat ? CULOARE_PAT : culoareSegment(s);
              const d = dec.get(s.id);
              const comun = {
                fill: "none", stroke: culoare, strokeWidth: lat * (ales ? 1.6 : 1),
                strokeDasharray: s.mediu === "sapa" ? "0.15 0.1" : undefined,
                strokeLinejoin: "round" as const, strokeLinecap: pat ? "butt" as const : "round" as const,
                opacity: estompat(s.flat) && !ales ? ESTOMPAT : pat ? 0.45 : 0.9,
                style: { cursor: "pointer" }, onClick: alege(s.id),
              };
              return (
                <g key={s.id}>
                  {d
                    ? s.puncte.slice(1).map((q, i) => {
                        // picior cu picior, fiecare deplasat în șirul lui de tuburi
                        const p = s.puncte[i];
                        const [dx, dy] = d[i] ?? [0, 0];
                        if (Math.hypot(q[0] - p[0], q[1] - p[1]) < 1e-6) return null;
                        return <line key={i} x1={p[0] + dx} y1={p[1] + dy} x2={q[0] + dx} y2={q[1] + dy} {...comun} />;
                      })
                    : <polyline points={s.puncte.map((p) => `${p[0]},${p[1]}`).join(" ")} {...comun} />}
                  {/* fitingurile: un cerc pe fiecare cot și ramificație, iar pe
                      segmentul ales și unghiul lor, cum le citește instalatorul */}
                  {(s.fitinguri ?? []).map((f, k) => {
                    const p = s.puncte[f.i];
                    if (!p) return null;
                    return (
                      <g key={k} style={{ cursor: "pointer" }} onClick={alege(s.id)}>
                        <circle cx={p[0]} cy={p[1]} r={lat * 0.9} fill={f.fel === "cot" ? "#fff" : culoareSegment(s)}
                                stroke={ales ? CULOARE_ALES : culoareSegment(s)} strokeWidth={lat * 0.35} />
                        {ales && (
                          <text x={p[0] + lat} y={p[1] - lat} fontSize={0.16} fill="#2b2622" fontWeight={600}
                                style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 0.05 }}>
                            {`${f.unghi}°`}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            })}
          {m.segmente.filter((s) => stratul(s) === strat && vertical(s) && alCorpului(s)
            && Math.min(s.puncte[0][2], s.puncte[1][2]) <= cotaSus
            && Math.max(s.puncte[0][2], s.puncte[1][2]) >= cotaJos)
            .sort((a, b) => Number(b.rol === "pat") - Number(a.rol === "pat"))
            .map((s) => {
              const ales = inst.ales === s.id || inst.evidentiate.has(s.id);
              const [x, y] = s.puncte[0];
              if (s.rol === "pat" && s.sectiune) {
                // patul vertical, văzut de sus: lățimea de-a lungul peretelui
                const [ux, uy] = s.directie ?? [1, 0];
                return (
                  <rect key={s.id} x={-s.sectiune.l / 2} y={-s.sectiune.h / 2} width={s.sectiune.l} height={s.sectiune.h}
                        transform={`translate(${x},${y}) rotate(${(Math.atan2(uy, ux) * 180) / Math.PI})`}
                        fill={CULOARE_PAT} fillOpacity={0.35} stroke={ales ? CULOARE_ALES : CULOARE_PAT} strokeWidth={0.015}
                        style={{ cursor: "pointer" }} onClick={alege(s.id)} />
                );
              }
              // un cablu se desenează cât e (Ø 1–2 cm): cele de pe coloană stau în grila lor
              const r = s.sistem === "electrice" ? Math.max(s.d_m, LATIME_MIN_CABLU_M) / 2 : Math.max(s.d_m, LATIME_MIN_M * 2) / 2;
              return (
                <circle key={s.id} cx={x} cy={y} r={r}
                        fill={ales ? CULOARE_ALES : culoareSegment(s)}
                        opacity={estompat(s.flat) ? ESTOMPAT : 1}
                        stroke="#fff" strokeWidth={0.005} style={{ cursor: "pointer" }} onClick={alege(s.id)} />
              );
            })}
        </g>
      ))}
      {m.echipamente.filter((e) => sistemAprins(e.sistem) && e.storey && niveluri.has(e.storey))
        .map((e) => (
          <rect key={e.id} x={-e.dim[0] / 2} y={-e.dim[1] / 2} width={e.dim[0]} height={e.dim[1]}
                transform={`translate(${e.centru[0]},${e.centru[1]}) rotate(${e.deg ?? 0})`}
                fill={e.fel === "ghena_propusa" ? "none" : e.fel === "doza" ? CULOARE[e.sistem] : "#fff"}
                stroke={inst.ales === e.id ? CULOARE_ALES : CULOARE[e.sistem]}
                strokeWidth={0.03} strokeDasharray={e.fel === "ghena_propusa" ? "0.08 0.05" : undefined}
                opacity={estompat(e.flat) ? ESTOMPAT : 1}
                style={{ cursor: "pointer" }} onClick={alege(e.id)} />
        ))}
    </g>
  );
}
